import { useTranslation } from 'next-i18next';

import { getAssetSrc, useResponsiveBreakPoints } from '@restworld/utils-common';
import { Heading, Text } from '@restworld/ui-ds-base';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { Wrapper } from '@restworld/ui-components';
import { ImageCarousel } from '../legacy';
import { MapComponent, IMapProps } from '../legacy';

import { defaultArgType } from './job-location.mocks';
import { CompanyProfileDrawer } from '../job-company-profile';

export type JobLocationMapBlockProps = IMapProps & Pick<JobLocationProps, 'locationDescription'>;

// Lightweight heuristic: any opening or self-closing HTML tag with an ASCII
// letter name is enough to route the string through the HTML pipeline.
// Anything else (plain text, with or without markdown-like punctuation) goes
// straight to <Paragraph> so it isn't reinterpreted by react-markdown.
const HTML_TAG_RE = /<\/?[a-z][^>]*>/i;
const containsHtmlTag = (value: string) => HTML_TAG_RE.test(value);

export function JobLocationBlock({
  locationDescription,
  restaurantBriefDescription,
  restaurantPictures,
  showRestaurant,
  children: applicationEntryPointButtonAsChild,
  ...rest
}: JobLocationProps) {
  const { isDesktop } = useResponsiveBreakPoints();
  const { t } = useTranslation('common');

  return (
    <Wrapper>
      {/* map block */}
      <JobLocationMapBlock {...rest} locationDescription={locationDescription} showRestaurant={showRestaurant} />
      {/* location pictures */}
      {restaurantPictures?.length && showRestaurant ? (
        <>
          <Heading size={5}>{t('restaurant_presentation')}</Heading>
          {/*
            ATS-imported restaurants (TeamTailor / Joule) deliver brief_description
            as raw HTML. CSM-edited restaurants (via Lab) deliver plain text.
            We branch so plain text doesn't get accidentally re-interpreted as HTML.
          */}
          {restaurantBriefDescription ? (
            containsHtmlTag(restaurantBriefDescription) ? (
              <Text dangerouslySetInnerHTML={{ __html: restaurantBriefDescription }} />
            ) : (
              <Text>{restaurantBriefDescription}</Text>
            )
          ) : null}
          <ImageCarousel restaurantPictures={restaurantPictures} />
        </>
      ) : null}
      {showRestaurant && (
        <CompanyProfileDrawer
          buttonVariant="secondary-neutral"
          buttonTitle={t('find_out_more_about_company')}
          buttonIcon={BuildingOfficeIcon}
        />
      )}
      {!isDesktop && <>{applicationEntryPointButtonAsChild}</>}
    </Wrapper>
  );
}
export const JobLocationMapBlock = ({ showRestaurant, locationDescription, ...rest }: JobLocationMapBlockProps) => {
  const { t } = useTranslation('job-position-page');
  return (
    <div className="flex flex-col gap-0">
      <span className="flex flex-row items-center gap-1.5 mb-4">
        <img src={getAssetSrc('/utils-assets/map-icon.svg')} className="h-6" alt="" />
        <Heading>{t('job_location_title')}</Heading>
      </span>
      <MapComponent {...rest} showRestaurant={showRestaurant} />
      <Text>{locationDescription}</Text>
    </div>
  );
};
export default JobLocationBlock;
export type JobLocationProps = defaultArgType;
